/* Rising Sun FPV - workshop tools (draft redesign).
   Filament finder, filament cost calculator, LiPo calculator and prop decoder.
   Vanilla JS, no dependencies, no storage. Product suggestions come from the store's own
   /collections/<handle>/products.json feed. */
(function () {
  'use strict';
  var d = document;

  /* ------------------------------------------------------------------ helpers */
  function $(sel, root) { return (root || d).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || d).querySelectorAll(sel)); }
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  function num(v, fb) { var n = parseFloat(v); return isFinite(n) ? n : fb; }
  function fmt(n, dp) { return n.toLocaleString('en-AU', { minimumFractionDigits: dp || 0, maximumFractionDigits: dp || 0 }); }
  function money(n) { return '$' + fmt(n, 2); }

  /* ------------------------------------------------------------------ tabs */
  function initTabs(root) {
    var tabs = $$('[data-rs-tooltab]', root);
    function select(tab, focus) {
      tabs.forEach(function (t) {
        var on = t === tab;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.setAttribute('tabindex', on ? '0' : '-1');
        var p = d.getElementById(t.getAttribute('aria-controls'));
        if (p) p.hidden = !on;
      });
      if (focus) tab.focus();
    }
    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () { select(tab); });
      tab.addEventListener('keydown', function (e) {
        var n = null;
        if (e.key === 'ArrowRight') n = tabs[(i + 1) % tabs.length];
        if (e.key === 'ArrowLeft') n = tabs[(i - 1 + tabs.length) % tabs.length];
        if (n) { e.preventDefault(); select(n, true); }
      });
    });
  }

  /* ------------------------------------------------------------------ filament finder data
     Ratings are 0-5 and deliberately conservative. They describe typical FDM behaviour. */
  var MATS = [
    { id: 'pla', name: 'PLA', col: 'pla', not: /silk|matte|\bcf\b|carbon|wood|glow/i,
      r: { easy: 5, detail: 5, strong: 2, flex: 0, heat: 1, outdoor: 1, stiff: 4, wear: 2, clear: 2 },
      temps: 'Nozzle 200–220°C, bed 55–60°C', enclosure: false,
      blurb: 'The easy all-rounder: crisp detail, low warping, prints on anything.',
      watch: ['Softens around 55–60°C, so keep it out of hot cars and direct summer sun.'] },
    { id: 'plaplus', name: 'PLA+ / Tough PLA', col: 'pla', only: /pla\+|pla plus|tough|pro\b|hyper|\bht\b/i, not: /silk|matte/i,
      r: { easy: 5, detail: 5, strong: 3, flex: 1, heat: 1, outdoor: 1, stiff: 4, wear: 2, clear: 1 },
      temps: 'Nozzle 205–225°C, bed 55–65°C', enclosure: false,
      blurb: 'Everything easy about PLA, with better impact strength for everyday parts.',
      watch: ['Still a PLA, so heat resistance is low.'] },
    { id: 'silk', name: 'Silk PLA', col: 'pla', only: /silk/i, finish: 'silk',
      r: { easy: 4, detail: 4, strong: 1, flex: 0, heat: 1, outdoor: 1, stiff: 3, wear: 1, clear: 1 },
      temps: 'Nozzle 205–225°C, bed 55–60°C', enclosure: false,
      blurb: 'Shiny, metallic-look finish straight off the printer. Great for gifts and display pieces.',
      watch: ['Weaker layer bonding than normal PLA, so use it for looks, not load.'] },
    { id: 'matte', name: 'Matte PLA', col: 'pla', only: /matte/i, finish: 'matte',
      r: { easy: 5, detail: 5, strong: 2, flex: 0, heat: 1, outdoor: 1, stiff: 3, wear: 1, clear: 0 },
      temps: 'Nozzle 200–220°C, bed 55–60°C', enclosure: false,
      blurb: 'Hides layer lines and takes primer well, ideal for models, terrain and cosplay.',
      watch: ['Slightly more brittle than standard PLA.'] },
    { id: 'petg', name: 'PETG', col: 'petg', not: /\bcf\b|carbon/i,
      r: { easy: 4, detail: 3, strong: 4, flex: 1, heat: 3, outdoor: 3, stiff: 3, wear: 3, clear: 3, water: 4, chem: 4 },
      temps: 'Nozzle 230–250°C, bed 70–85°C', enclosure: false,
      blurb: 'Tough, slightly flexible and moisture resistant. The go-to for functional parts.',
      watch: ['Can string. Dry the spool if it pops or crackles while printing.'] },
    { id: 'pctg', name: 'PCTG', col: 'pctg', only: /pctg/i,
      r: { easy: 3, detail: 3, strong: 5, flex: 2, heat: 3, outdoor: 3, stiff: 3, wear: 3, clear: 4, water: 4, chem: 4 },
      temps: 'Nozzle 240–260°C, bed 70–85°C', enclosure: false,
      blurb: 'A tougher, clearer cousin of PETG with excellent impact strength.',
      watch: ['Runs a little hotter than PETG.'] },
    { id: 'asa', name: 'ASA', col: 'asa', only: /asa/i,
      r: { easy: 2, detail: 3, strong: 4, flex: 1, heat: 4, outdoor: 5, stiff: 3, wear: 3, clear: 1, water: 4, chem: 4 },
      temps: 'Nozzle 240–260°C, bed 90–110°C', enclosure: true,
      blurb: 'Built for the outdoors: UV-stable, weatherproof and heat resistant.',
      watch: ['Needs an enclosure and good ventilation, or large parts will warp and crack.'] },
    { id: 'abs', name: 'ABS', col: 'asa', only: /\babs\b/i,
      r: { easy: 2, detail: 3, strong: 4, flex: 1, heat: 4, outdoor: 2, stiff: 3, wear: 3, clear: 1, chem: 3 },
      temps: 'Nozzle 240–260°C, bed 90–110°C', enclosure: true,
      blurb: 'Classic engineering plastic for heat-resistant indoor parts. Can be vapour smoothed.',
      watch: ['Needs an enclosure and ventilation.', 'Yellows and goes brittle in strong sun, so choose ASA for outdoors.'] },
    { id: 'tpu', name: 'TPU 95A', col: 'tpu',
      r: { easy: 2, detail: 2, strong: 5, flex: 5, heat: 2, outdoor: 3, stiff: 0, wear: 4, clear: 1, water: 3, chem: 3 },
      temps: 'Nozzle 210–230°C, bed 40–60°C, print slowly', enclosure: false,
      blurb: 'Rubbery and nearly unbreakable. It is what FPV pilots print mounts, bumpers and antenna holders from.',
      watch: ['Print slowly (20–40 mm/s). A direct-drive extruder makes life much easier.'] },
    { id: 'peba', name: 'PEBA', col: 'peba',
      r: { easy: 2, detail: 2, strong: 5, flex: 5, heat: 2, outdoor: 3, stiff: 0, wear: 4, clear: 0 },
      temps: 'Check the spool label', enclosure: false,
      blurb: 'Lighter and springier than TPU with great rebound, ideal for crash-prone FPV parts.',
      watch: ['Very flexible: print slowly and keep the filament path short.'] },
    { id: 'nylon', name: 'Nylon (PA)', col: 'nylon', not: /\bcf\b|carbon|\bgf\b|glass/i,
      r: { easy: 1, detail: 3, strong: 5, flex: 2, heat: 4, outdoor: 2, stiff: 3, wear: 5, clear: 0, chem: 4 },
      temps: 'Nozzle 250–280°C, bed 70–90°C', enclosure: true, dry: true,
      blurb: 'The toughest common filament, slippery and wear resistant. Perfect for gears, hinges and bushings.',
      watch: ['Soaks up moisture fast. Dry before printing and store it sealed.', 'An enclosure is strongly recommended.'] },
    { id: 'pc', name: 'Polycarbonate (PC)', col: 'pc',
      r: { easy: 1, detail: 3, strong: 5, flex: 1, heat: 5, outdoor: 3, stiff: 4, wear: 3, clear: 3, chem: 3 },
      temps: 'Nozzle 260–300°C, bed 100–120°C', enclosure: true, dry: true,
      blurb: 'Highest heat resistance and impact strength you can print on most machines.',
      watch: ['Needs high temperatures and an enclosure.', 'Dry it before printing.'] },
    { id: 'cf', name: 'Carbon-fibre filled (PETG-CF / PA-CF)', cols: ['nylon', 'petg', 'pla'], only: /\bcf\b|carbon/i, hardened: true,
      r: { easy: 3, detail: 3, strong: 4, flex: 0, heat: 4, outdoor: 4, stiff: 5, wear: 4, clear: 0 },
      temps: 'Follow the base polymer, and use a hardened nozzle', enclosure: false, finish: 'matte',
      blurb: 'Stiff, light and dimensionally stable with a matte finish. Great for brackets, jigs and frames.',
      watch: ['Abrasive: it chews through brass nozzles, so use hardened steel.'] },
    { id: 'pvb', name: 'PVB (smoothable)', col: 'pvb', finish: 'glossy',
      r: { easy: 3, detail: 4, strong: 2, flex: 0, heat: 2, outdoor: 1, stiff: 3, wear: 1, clear: 5 },
      temps: 'Check the spool label', enclosure: false,
      blurb: 'Prints like PLA, then smooths to a glossy, near-clear finish with isopropyl alcohol.',
      watch: ['Low heat resistance, similar to PLA.'] }
  ];

  var NEED_LABEL = { easy: 'easy to print', detail: 'fine detail', strong: 'strength and toughness', flex: 'flexibility', heat: 'heat resistance', outdoor: 'outdoor and UV resistance', stiff: 'stiffness', wear: 'wear resistance', clear: 'clarity', water: 'water resistance', chem: 'chemical resistance' };
  var GOOD = {
    easy: 'Easy to print with few failures',
    detail: 'Holds fine detail and prints cleanly',
    strong: 'Tough and impact resistant',
    flex: 'Flexes and bounces back instead of snapping',
    heat: 'Copes with heat far better than PLA',
    outdoor: 'Stands up to sun and weather',
    stiff: 'Stiff and rigid for its weight',
    wear: 'Resists wear and rubbing',
    clear: 'Can print clear or translucent',
    water: 'Shrugs off water and moisture',
    chem: 'Good chemical and oil resistance'
  };
  var MID = {
    heat: 'Handles more heat than PLA, but not a scorching dashboard',
    outdoor: 'Copes with some sun and weather',
    strong: 'Reasonably tough',
    water: 'Handles moisture well enough',
    wear: 'Moderate wear resistance'
  };
  var WEAK = {
    heat: 'Low heat resistance for this job',
    outdoor: 'Not ideal for long-term outdoor use',
    flex: 'Rigid, not flexible',
    strong: 'Not the toughest option',
    easy: 'Harder to print well',
    detail: 'Detail is not its strength'
  };

  /* Words in the description -> needs. Each hit adds weight to that need. */
  var KEYWORDS = [
    [/\b(outdoors?|outside|sun|sunny|uv|weather|garden|rain|roof|balcony|boat|marine|pool|letterbox|mailbox)\b/i, { outdoor: 3 }],
    [/\b(heat|hot|car|dash|dashboard|engine|exhaust|kettle|oven|high temp|motor mount|near (a |the )?motor|townsville)\b/i, { heat: 3 }],
    [/\b(flex|flexible|soft|rubber|rubbery|bumper|gasket|seal|grip|phone case|tyre|tire|shock|damp|squishy|bouncy|strap|fidget|crash|crashes|antenna mount|antenna holder|gopro mount|camera mount|xt60 cap|tpu)\b/i, { flex: 3, strong: 1 }],
    [/\b(strong|tough|durable|functional|bracket|mount|hinge|clip|hook|load|jig|fixture|replacement part|mechanical|impact|drop|holder|stand|tool)\b/i, { strong: 2 }],
    [/\b(gears?|bearing|bushing|pulley|wear|sliding|abrasion|wheel|cog|sprocket)\b/i, { wear: 3, strong: 1 }],
    [/\b(miniatures?|minis?|figures?|figurines?|statues?|busts?|display|models?|terrain|scenery|cosplay|props?|decor|ornaments?|art|sculpture|toy|detailed|detail|gift|helmet|armou?r|mask)\b/i, { detail: 3 }],
    [/\b(beginner|first print|easy|new to|simple|kids?|school|classroom|prototype|test print|cheap)\b/i, { easy: 3 }],
    [/\b(stiff|rigid|lightweight|light weight|chassis|carbon|frame|arms?)\b/i, { stiff: 3 }],
    [/\b(chemical|fuel|oil|petrol|solvent|acid)\b/i, { chem: 3 }],
    [/\b(clear|transparent|translucent|diffuser|lamp ?shade|light ?box|window|see.through)\b/i, { clear: 3 }],
    [/\b(water|wet|waterproof|bath|shower|fish tank|aquarium|planter|plant pot|pot|vase|rain)\b/i, { water: 3 }],
    [/\b(shiny|silk|metallic|gold|silver|copper|bronze)\b/i, { finish_silk: 3, detail: 1 }],
    [/\b(matte|hide layer|layer lines|primer|sand|paint)\b/i, { finish_matte: 2, detail: 1 }],
    [/\b(glossy|smooth finish)\b/i, { finish_glossy: 3 }]
  ];
  var FOOD = /\b(food|cup|mug|plate|cookie cutter|drink|bowl|cutlery|straw|baby)\b/i;
  var MINI = /\b(miniatures?|minis\b|28mm|32mm|d&d mini|warhammer|tabletop figure)\b/i;
  var FPV = /\b(fpv|drone|quad|whoop|cinewhoop)\b/i;

  function readNeeds(form) {
    var q = (form.q.value || '').trim();
    var needs = {}, hits = [];
    function add(k, w) { needs[k] = (needs[k] || 0) + w; }
    KEYWORDS.forEach(function (kw) {
      var m = q.match(kw[0]);
      if (m) { Object.keys(kw[1]).forEach(function (k) { add(k, kw[1][k]); }); hits.push(m[0].toLowerCase()); }
    });
    $$('input[name="need"]:checked', form).forEach(function (c) { add(c.value, 4); });
    return {
      q: q, needs: needs, hits: hits,
      enclosed: form.enclosed.checked, hardened: form.hardened.checked,
      food: FOOD.test(q), mini: MINI.test(q), fpv: FPV.test(q)
    };
  }

  function score(m, input) {
    var needs = input.needs, keys = Object.keys(needs), total = 0, max = 0;
    if (!keys.length) { needs = { easy: 2, strong: 1 }; keys = Object.keys(needs); }
    keys.forEach(function (k) {
      var w = needs[k];
      if (k.indexOf('finish_') === 0) {
        max += w * 5;
        if (m.finish === k.slice(7)) total += w * 5;
        return;
      }
      max += w * 5;
      total += w * (m.r[k] != null ? m.r[k] : 2);
    });
    var s = max ? total / max : 0;
    if (m.enclosure && !input.enclosed) s *= 0.8;
    if (m.hardened && !input.hardened) s *= 0.55;
    if (needs.flex >= 3 && m.r.flex <= 1) s *= 0.6;
    if (!(needs.flex > 0) && m.r.flex >= 4) s *= 0.65;
    return Math.max(0, Math.min(1, s));
  }

  function whyLines(m, input) {
    var good = [], weak = [];
    Object.keys(input.needs).sort(function (a, b) { return input.needs[b] - input.needs[a]; }).forEach(function (k) {
      if (k.indexOf('finish_') === 0) {
        if (m.finish === k.slice(7)) good.push(k === 'finish_silk' ? 'Shiny silk finish, no painting needed' : k === 'finish_matte' ? 'Matte finish that hides layer lines' : 'Smooths to a glossy finish');
        return;
      }
      var v = m.r[k];
      if (v == null) return;
      if (v >= 4 && GOOD[k] && good.indexOf(GOOD[k]) < 0) good.push(GOOD[k]);
      else if (v === 3 && MID[k] && input.needs[k] >= 3) good.push(MID[k]);
      else if (v <= 1 && WEAK[k]) weak.push(WEAK[k]);
    });
    return { good: good.slice(0, 3), weak: weak.slice(0, 2) };
  }

  /* ------------------------------------------------------------------ product lookups (cached per collection) */
  var cache = {};
  function loadCollection(handle) {
    if (!handle) return Promise.resolve([]);
    if (!cache[handle]) {
      cache[handle] = fetch('/collections/' + encodeURIComponent(handle) + '/products.json?limit=60')
        .then(function (r) { return r.ok ? r.json() : { products: [] }; })
        .then(function (j) { return j.products || []; })
        .catch(function () { return []; });
    }
    return cache[handle];
  }
  function productsFor(m, cfg) {
    var handles = (m.cols || [m.col]).map(function (c) { return cfg.collections[c]; });
    return Promise.all(handles.map(loadCollection)).then(function (lists) {
      var seen = {}, out = [];
      lists.forEach(function (l) { l.forEach(function (p) { if (!seen[p.id]) { seen[p.id] = 1; out.push(p); } }); });
      out = out.filter(function (p) {
        if (m.only && !m.only.test(p.title)) return false;
        if (m.not && m.not.test(p.title)) return false;
        return true;
      });
      out.forEach(function (p) {
        p._avail = (p.variants || []).some(function (v) { return v.available; });
        p._nova = /nova/i.test(p.title) || /rising sun/i.test(p.vendor || '');
      });
      out.sort(function (a, b) { return (b._avail - a._avail) || (b._nova - a._nova); });
      return out.slice(0, 3);
    });
  }
  function productCard(p) {
    var img = p.images && p.images[0] ? p.images[0].src : '';
    if (img) img += (img.indexOf('?') > -1 ? '&' : '?') + 'width=240';
    var prices = (p.variants || []).map(function (v) { return parseFloat(v.price); }).filter(isFinite);
    var min = prices.length ? Math.min.apply(null, prices) : null;
    return '<a class="rs-mini" href="/products/' + esc(p.handle) + '">' +
      (img ? '<img src="' + esc(img) + '" alt="" loading="lazy" width="120" height="120">' : '') +
      '<span class="rs-mini__t">' + esc(p.title) + '</span>' +
      '<span class="rs-mini__p">' + (min != null ? (prices.length > 1 && Math.max.apply(null, prices) > min ? 'From ' : '') + money(min) : '') +
      (p._avail ? '' : ' <em>sold out</em>') + '</span></a>';
  }

  function initFinder(box, cfg) {
    var form = $('[data-rs-finder-form]', box);
    var out = $('[data-rs-finder-out]', box);
    if (!form || !out) return;

    $$('[data-rs-example]', box).forEach(function (b) {
      b.addEventListener('click', function () {
        form.q.value = b.getAttribute('data-rs-example');
        run();
      });
    });
    form.addEventListener('submit', function (e) { e.preventDefault(); run(); });

    function run() {
      var input = readNeeds(form);
      var ranked = MATS.map(function (m) { return { m: m, s: score(m, input) }; })
        .sort(function (a, b) { return b.s - a.s; })
        .slice(0, 3);

      var chips = Object.keys(input.needs).filter(function (k) { return k.indexOf('finish_') < 0; })
        .sort(function (a, b) { return input.needs[b] - input.needs[a]; })
        .map(function (k) { return '<span class="rs-tagline">' + esc(NEED_LABEL[k] || k) + '</span>'; }).join('');
      var html = '<div class="rs-finder__head"><p class="rs-finder__sys">&gt; ANALYSIS COMPLETE</p>' +
        (chips ? '<p class="rs-finder__detected">Looking for: ' + chips + '</p>' : '<p class="rs-finder__detected">No specific needs given, so these are the best all-rounders.</p>') + '</div>';

      if (input.food) {
        html += '<p class="rs-alert">Heads up: FDM prints aren\'t considered food safe, because bacteria can live in the layer lines. For anything that touches food, use it once or line it, and ask us before you print.</p>';
      }

      html += '<ol class="rs-results">';
      ranked.forEach(function (x, i) {
        var m = x.m, w = whyLines(m, input), pct = Math.round(x.s * 100);
        var watch = m.watch.slice();
        if (m.enclosure && !input.enclosed) watch.unshift('You said no enclosure, so expect warping on larger parts.');
        if (m.hardened && !input.hardened) watch.unshift('You\'ll need a hardened steel nozzle for this one.');
        html += '<li class="rs-result">' +
          '<div class="rs-result__top"><span class="rs-result__rank">#' + (i + 1) + '</span>' +
          '<h3 class="rs-result__name">' + esc(m.name) + '</h3>' +
          '<span class="rs-result__score" aria-label="Match ' + pct + ' percent"><span class="rs-meter"><span style="width:' + pct + '%"></span></span><b>' + pct + '%</b></span></div>' +
          '<p class="rs-result__blurb">' + esc(m.blurb) + '</p>' +
          (w.good.length ? '<ul class="rs-result__why">' + w.good.map(function (g) { return '<li>' + esc(g) + '</li>'; }).join('') + '</ul>' : '') +
          ((w.weak.length || watch.length) ? '<ul class="rs-result__watch">' + w.weak.concat(watch).slice(0, 3).map(function (g) { return '<li>' + esc(g) + '</li>'; }).join('') + '</ul>' : '') +
          '<p class="rs-result__temps"><span>Typical settings</span> ' + esc(m.temps) + '</p>' +
          '<div class="rs-result__products" data-rs-products="' + esc(m.id) + '"><span class="rs-result__loading">Checking the shelves…</span></div>' +
          '</li>';
      });
      html += '</ol>';
      if (input.mini) {
        html += '<p class="rs-alert rs-alert--info">Printing 28–32mm miniatures? A resin printer captures far finer detail than any filament. <a href="/collections/' + esc(cfg.collections.resin) + '">Browse resin</a></p>';
      }
      if (input.fpv && !(input.needs.flex > 0)) {
        html += '<p class="rs-alert rs-alert--info">Printing FPV parts? Mounts and bumpers that take crashes are almost always printed in TPU.</p>';
      }
      html += '<p class="rs-finder__foot">Typical ranges only, so always check the spool label. Not sure? <a href="' + esc(cfg.helpUrl) + '">Ask the crew</a>, we print every day.</p>';
      out.innerHTML = html;
      out.classList.remove('is-anim'); void out.offsetWidth; out.classList.add('is-anim');

      ranked.forEach(function (x) {
        var slot = out.querySelector('[data-rs-products="' + x.m.id + '"]');
        productsFor(x.m, cfg).then(function (list) {
          var col = cfg.collections[(x.m.cols || [x.m.col])[0]];
          slot.innerHTML = (list.length ? '<div class="rs-mini-row">' + list.map(productCard).join('') + '</div>' : '') +
            '<a class="rs-link rs-result__shop" href="/collections/' + esc(col || cfg.collections.all) + '">Shop ' + esc(x.m.name.split(' (')[0]) + ' &rarr;</a>';
        });
      });
    }
  }

  /* ------------------------------------------------------------------ filament cost calculator */
  function initFilamentCalc(box) {
    var form = $('form', box), out = $('[data-rs-fcalc-out]', box);
    function calc() {
      var dens = num(form.density.value, 1.24), dia = num(form.dia.value, 1.75);
      var g = Math.max(0, num(form.grams.value, 0)), price = Math.max(0, num(form.price.value, 0)), spool = Math.max(1, num(form.spool.value, 1000));
      var area = Math.PI * Math.pow(dia / 2, 2); /* mm2 */
      var gPerM = area * dens; /* mm2 x 1000mm / 1000 (cm3) x g/cm3 = area x dens g/m */
      var metres = g / gPerM, cost = g / spool * price;
      var spoolM = spool / gPerM, per = g > 0 ? Math.floor(spool / g) : 0;
      out.innerHTML =
        '<div class="rs-readout__big"><span>Print cost</span><b>' + money(cost) + '</b></div>' +
        '<dl class="rs-readout__grid">' +
        '<div><dt>Filament used</dt><dd>' + fmt(metres, 1) + ' m</dd></div>' +
        '<div><dt>Share of spool</dt><dd>' + fmt(g / spool * 100, 1) + '%</dd></div>' +
        '<div><dt>Prints per spool</dt><dd>' + (per ? fmt(per) : '–') + '</dd></div>' +
        '<div><dt>Whole spool length</dt><dd>' + fmt(spoolM) + ' m</dd></div>' +
        '<div><dt>Weight per metre</dt><dd>' + fmt(gPerM, 2) + ' g</dd></div>' +
        '<div><dt>Cost per metre</dt><dd>' + money(price / spoolM) + '</dd></div>' +
        '</dl>';
    }
    form.addEventListener('input', calc);
    form.addEventListener('change', calc);
    form.addEventListener('submit', function (e) { e.preventDefault(); });
    calc();
  }

  /* ------------------------------------------------------------------ LiPo calculator */
  var CHEM = {
    lipo: { full: 4.2, storage: 3.8, nominal: 3.7, land: 3.5, label: 'LiPo' },
    lihv: { full: 4.35, storage: 3.85, nominal: 3.8, land: 3.5, label: 'LiHV' },
    liion: { full: 4.2, storage: 3.7, nominal: 3.6, land: 3.0, label: 'Li-ion' }
  };
  function initLipo(box) {
    var form = $('form', box), out = $('[data-rs-lipo-out]', box), custom = $('[data-rs-lipo-custom]', box);
    function calc() {
      var cells = num(form.cells.value, 6), mah = Math.max(0, num(form.mah.value, 0));
      var c = CHEM[form.chem.value] || CHEM.lipo;
      var isCustom = form.style.value === 'custom';
      custom.hidden = !isCustom;
      var amps = isCustom ? Math.max(0.5, num(form.amps.value, 18)) : num(form.style.value, 18);
      if (!isCustom) form.amps.value = amps;
      var ah = mah / 1000;
      var minutes = amps > 0 ? ah * 0.8 / amps * 60 : 0;
      var wh = cells * c.nominal * ah;
      var mm = Math.floor(minutes), ss = Math.round((minutes - mm) * 60);
      if (ss === 60) { mm++; ss = 0; }
      out.innerHTML =
        '<div class="rs-readout__big"><span>Estimated flight time</span><b>' + mm + ':' + (ss < 10 ? '0' : '') + ss + ' <small>min</small></b></div>' +
        '<dl class="rs-readout__grid">' +
        '<div><dt>Fully charged</dt><dd>' + fmt(cells * c.full, 2) + ' V</dd></div>' +
        '<div><dt>Storage charge</dt><dd>' + fmt(cells * c.storage, 2) + ' V <small>(' + c.storage.toFixed(2) + ' V/cell)</small></dd></div>' +
        '<div><dt>Nominal</dt><dd>' + fmt(cells * c.nominal, 1) + ' V</dd></div>' +
        '<div><dt>Land by about</dt><dd>' + fmt(cells * c.land, 1) + ' V <small>under load</small></dd></div>' +
        '<div><dt>Energy</dt><dd>' + fmt(wh, 1) + ' Wh</dd></div>' +
        '<div><dt>1C charge rate</dt><dd>' + fmt(ah, 2) + ' A</dd></div>' +
        '</dl>' +
        '<p class="rs-readout__flag' + (wh <= 100 ? ' is-ok' : ' is-warn') + '">' +
        (wh <= 100 ? 'Under 100 Wh: generally allowed in airline carry-on. Check your airline first.' : 'Over 100 Wh: most airlines need approval or won\'t carry it.') + '</p>';
    }
    form.addEventListener('input', calc);
    form.addEventListener('change', calc);
    form.addEventListener('submit', function (e) { e.preventDefault(); });
    calc();
  }

  /* ------------------------------------------------------------------ prop decoder */
  function parseProp(raw) {
    var s = String(raw || '').toLowerCase().replace(/[×*]/g, 'x').replace(/,/g, '.');
    var dia = null, pitch = null, blades = null, m;
    if ((m = s.match(/(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)(?:\s*x\s*(\d))?/))) {
      dia = parseFloat(m[1]); pitch = parseFloat(m[2]); if (m[3]) blades = parseInt(m[3], 10);
    } else if ((m = s.match(/(\d{2,3}(?:\.\d+)?)\s*mm/))) {
      dia = parseFloat(m[1]) / 25.4;
    } else if ((m = s.match(/(?:^|[^\d])(\d{4,5})(?:\.\d+)?(?:\s*[-_ ]\s*(\d))?(?!\d)/))) {
      var digits = m[1];
      dia = parseInt(digits.slice(0, 2), 10) / 10;
      pitch = digits.length === 5 ? parseInt(digits.slice(2), 10) / 100 : parseInt(digits.slice(2), 10) / 10;
      if (m[2]) blades = parseInt(m[2], 10);
    }
    if (blades == null) {
      if ((m = s.match(/(\d)\s*[- ]?\s*blades?/))) blades = parseInt(m[1], 10);
      else if (/\btri\b|tri-?blade/.test(s)) blades = 3;
      else if (/\bquad\b/.test(s)) blades = 4;
      else if (/\bbi\b|bi-?blade/.test(s)) blades = 2;
      else if ((m = s.match(/v\d?s?\s*$|-(\d)\s*$/)) && m[1]) blades = parseInt(m[1], 10);
    }
    if (!dia || dia > 30) return null;
    return { dia: dia, pitch: pitch, blades: blades };
  }
  function classify(dia) {
    var rows = [
      [1.35, '65–75 mm tiny whoop', '0702–0802', 'tinywhoop-props'],
      [1.75, '75–85 mm whoop', '0802–1002', 'tinywhoop-props'],
      [2.2, '2" micro or toothpick', '1103–1204', '2-props'],
      [2.7, '2.5" micro', '1303–1404', '3-props'],
      [3.3, '3" micro or cinewhoop', '1404–1507', '3-props'],
      [4.3, '4" (light long range)', '1804–2004', '4-props'],
      [5.05, '5" freestyle or race', '2207–2306', '5-props'],
      [5.35, '5" freestyle (5.1")', '2207–2306', '5-1-props'],
      [5.7, '5.5" frame', '2306–2207', '5-5-props'],
      [6.4, '6" frame', '2306–2507', '6-props'],
      [7.6, '7" long range', '2806.5–2807', '7-props'],
      [99, '8"+ or X-class', '3110 and up', '8-13-props']
    ];
    for (var i = 0; i < rows.length; i++) if (dia <= rows[i][0]) return { frame: rows[i][1], motor: rows[i][2], col: rows[i][3] };
    return null;
  }
  function initProp(box, cfg) {
    var form = $('form', box), out = $('[data-rs-prop-out]', box);
    $$('[data-rs-prop-eg]', box).forEach(function (b) {
      b.addEventListener('click', function () { form.prop.value = b.getAttribute('data-rs-prop-eg'); calc(); });
    });
    function calc() {
      var p = parseProp(form.prop.value);
      if (!p) {
        out.innerHTML = '<p class="rs-readout__flag is-warn">Couldn\'t read that one. Try a code like 5143-3 or a size like 5x4.3x3.</p>';
        return;
      }
      var k = classify(p.dia);
      var pitchNote = '';
      if (p.pitch != null) {
        var ratio = p.pitch / p.dia;
        pitchNote = ratio < 0.6 ? 'Low pitch: smooth and efficient, good for cruising and cinematic flying.' :
          ratio > 0.9 ? 'High pitch: more top speed and punch, but more current draw and heat.' :
          'Middle-of-the-road pitch: a balanced freestyle feel.';
      }
      out.innerHTML =
        '<div class="rs-readout__big"><span>Decoded</span><b>' + fmt(p.dia, 1) + '"' + (p.pitch != null ? ' × ' + fmt(p.pitch, Math.round(p.pitch * 100) % 10 ? 2 : 1) + '"' : '') + (p.blades ? ' × ' + p.blades + ' blades' : '') + '</b></div>' +
        '<dl class="rs-readout__grid">' +
        '<div><dt>Diameter</dt><dd>' + fmt(p.dia, 2) + ' in / ' + fmt(p.dia * 25.4) + ' mm</dd></div>' +
        '<div><dt>Pitch</dt><dd>' + (p.pitch != null ? fmt(p.pitch, 2) + ' in per turn' : 'not in the name') + '</dd></div>' +
        '<div><dt>Blades</dt><dd>' + (p.blades || 'not in the name') + '</dd></div>' +
        (k ? '<div><dt>Suits</dt><dd>' + esc(k.frame) + '</dd></div><div><dt>Typical motors</dt><dd>' + esc(k.motor) + '</dd></div>' : '') +
        '</dl>' +
        (pitchNote && p.dia >= 2 ? '<p class="rs-readout__flag is-ok">' + esc(pitchNote) + '</p>' : '') +
        (p.dia < 2 && p.pitch != null ? '<p class="rs-readout__flag">Whoop and micro prop codes don\'t always follow the pitch convention, so check the listing for the real pitch.</p>' : '') +
        '<p class="rs-readout__links">' + (k ? '<a class="rs-link" href="/collections/' + esc(k.col) + '">Shop props this size &rarr;</a>' : '') +
        ' <a class="rs-link" href="' + esc(cfg.propGuide) + '">How prop names work &rarr;</a></p>';
    }
    form.addEventListener('input', calc);
    form.addEventListener('submit', function (e) { e.preventDefault(); calc(); });
    calc();
  }

  /* ------------------------------------------------------------------ boot */
  function init(root) {
    $$('[data-rs-tools]', root).forEach(function (sec) {
      if (sec.dataset.rsInit) return;
      sec.dataset.rsInit = '1';
      var cfg = { collections: {}, propGuide: '#', helpUrl: '/pages/contact-us' };
      try { cfg = JSON.parse($('[data-rs-tools-config]', sec).textContent); } catch (e) { /* keep defaults */ }
      initTabs(sec);
      var f = $('[data-rs-finder]', sec); if (f) initFinder(f, cfg);
      var fc = $('[data-rs-fcalc]', sec); if (fc) initFilamentCalc(fc);
      var lp = $('[data-rs-lipo]', sec); if (lp) initLipo(lp);
      var pr = $('[data-rs-prop]', sec); if (pr) initProp(pr, cfg);
    });
  }
  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', function () { init(d); }); else init(d);
  d.addEventListener('shopify:section:load', function (e) { init(e.target); });

  /* exposed for testing */
  window.RSTools = { parseProp: parseProp, score: score, readNeeds: readNeeds, classify: classify, MATS: MATS };
})();
